import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
const _excluded = ["step", "valueLabelDisplay", "valueLabelFormat", "scale", "disabled", "name"];
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
/* eslint-disable @typescript-eslint/no-explicit-any */
import * as React from 'react';
import { clsx } from 'clsx';
import { Forward } from '../../../internal/components/Forward';
import { useSliderController } from './useSliderController';
import styles from './styles.module.css';
import { SliderValueLabelPopover } from './SliderValueLabelPopover';
import { SliderMarks } from './SliderMarks';
import { formatNumber } from '../../../internal/utils/formatNumber';
import { identity } from 'ramda';

/**
 * Слайдер с ползунками
 *
 * Поддерживает как одиночные, так и множественные значения
 *
 * На сервере, в целях оптимизации, не рендерятся разделители (marks)
 *
 * [Ссылка на макет](https://www.figma.com/design/4UM8zoowH9YGMM7fSK0rhD?node-id=22838-14787#1026366045)
 *
 * @export
 * @template T extends `number | [number, number, ...number[]]`
 * @param {ISliderProps<T>} props
 */
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
export function Slider(_ref) {
  let {
      step = 1,
      valueLabelDisplay = 'auto',
      valueLabelFormat = identity,
      scale = identity,
      disabled,
      name
    } = _ref,
    props = _objectWithoutProperties(_ref, _excluded);
  const {
    sliderRef,
    valueState,
    trackOffset,
    trackLeap,
    onMouseDown,
    thumbs,
    marks
  } = useSliderController(_objectSpread({
    step,
    scale,
    valueLabelDisplay,
    valueLabelFormat
  }, props));
  const {
    values,
    valueDerived
  } = valueState;
  const marked = marks.some(_ref2 => {
    let {
      label
    } = _ref2;
    return !!label;
  });
  const SliderValueLabelComponent = valueLabelDisplay === 'off' ? Forward : SliderValueLabelPopover;
  return /*#__PURE__*/_jsxs("span", {
    ref: sliderRef,
    className: clsx(styles['slider'], marked && styles['marked'], disabled && styles['disabled'], props.__theme === 'input' && styles['internal-theme-input']),
    style: {
      width: props.width
    },
    onMouseDown: onMouseDown,
    children: [/*#__PURE__*/_jsx("span", {
      className: styles['rail']
    }), /*#__PURE__*/_jsx("span", {
      className: styles['track'],
      style: {
        left: "".concat(trackOffset, "%"),
        width: "".concat(trackLeap, "%")
      }
    }), name && /*#__PURE__*/_jsx("input", {
      type: "hidden",
      name: name,
      value: valueDerived,
      disabled: disabled
    }), /*#__PURE__*/_jsx(SliderMarks, _objectSpread(_objectSpread({
      step: step,
      valueLabelDisplay: valueLabelDisplay,
      valueLabelFormat: valueLabelFormat,
      scale: scale
    }, props), {}, {
      values: values,
      marks: marks
    })), thumbs.map((thumb, index) => {
      return /*#__PURE__*/_jsx(SliderValueLabelComponent, {
        open: thumb.open || valueLabelDisplay === 'on',
        label: formatNumber(thumb.value),
        children: /*#__PURE__*/_jsx("span", {
          role: "slider",
          "aria-orientation": "horizontal",
          "data-index": index,
          style: {
            left: "".concat(thumb.percent, "%")
          },
          className: clsx(styles['thumb'], thumb.focusVisible && styles['focus-visible'], thumb.active && styles['active']),
          onFocus: thumb.onFocus,
          onBlur: thumb.onBlur,
          onMouseOver: thumb.onMouseOver,
          onMouseLeave: thumb.onMouseLeave,
          onKeyDown: thumb.onKeyDown,
          tabIndex: disabled ? undefined : props.tabIndex || 0
        })
      }, index);
    })]
  });
}